import { setData } from '@/actions/auth';
import {
  addCourseToWishList,
  getCoursesByPage,
  setCourseEditData,
  setCourseQueryData,
} from '@/actions/courses';
import CustomToolTip from '@/components/CusotmTooltip';
import { capitalizeFirstLetter } from '@/helpers/dataHelper';
import useWidth, { IBooleanArgs } from '@/helpers/widthUtil';
import { ArrowRightOutlined, HeartFilled, HeartOutlined } from '@ant-design/icons';
import { Badge, Card, Empty, Modal, Rate, Skeleton } from 'antd';
import { NextRouter, useRouter } from 'next/router';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Col, Spinner } from 'reactstrap';
import { Dispatch } from 'redux';
import { isLogin } from '@/helpers/loginHelper';
import { setProfileData } from '@/actions/profile';
import Onboarding from '../onboarding';

type CourseItemGridProps = { total: number; loading: boolean };

const CourseItemGrid = ({ total, loading }: CourseItemGridProps) => {
  const dispatch: Dispatch = useDispatch();
  const { isMobile, isTablet }: IBooleanArgs = useWidth();
  const router: NextRouter = useRouter();
  const {
    coursesData,
    fetchCourse,
    courseMeta,
    getMoreCoursesStatus,
    courseLoading,
    courseQuery,
    addCourseWishlistStatus,
    wishlistStatus,
    profileWishlist,
  } = useSelector((state: RootStateOrAny) => ({
    fetchCourse: state.courses.course,
    courseMeta: state?.courses?.course?.meta,
    coursesData: state?.courses?.course?.data,
    getMoreCoursesStatus: state?.courses?.getMoreCoursesStatus,
    courseLoading: state?.courses?.courseLoading,
    courseQuery: state?.courses?.query?.courses,
    addCourseWishlistStatus: state?.courses?.addCourseWishlistStatus,
    wishlistStatus: state?.courses?.courseDetail?.data?.wishlist,
    profileWishlist: state?.profile?.wishlist?.meta,
  }));

  const courseObserver: any = useRef();
  const [hasMoreCourse, setHasMoreCourse] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      setHasMoreCourse(false);
    };
  }, []);

  useEffect(() => {
    if (addCourseWishlistStatus === 'success') {
      dispatch(
        setProfileData({
          name: 'wishlist',
          key: 'meta',
          value: {
            ...profileWishlist,
            total: wishlistStatus
              ? profileWishlist?.total + 1
              : profileWishlist?.total > 0
              ? profileWishlist?.total - 1
              : 0,
          },
        })
      );
    }
    setTimeout(
      () =>
        dispatch(
          setCourseEditData({
            key: 'addCourseWishlistStatus',
            value: '',
          })
        ),
      3000
    );
  }, [addCourseWishlistStatus]);

  useEffect(() => {
    setHasMoreCourse(courseMeta?.last_pages === courseQuery?.page ? false : true);
  }, [courseMeta]);

  const findCourseObject = (id: number): any => {
    return fetchCourse?.data?.find((each: any) => each.id === id);
  };

  const { loginModal } = useSelector(
    (state: RootStateOrAny) => state.auth.helperObj
  );

  const lastCourseElementRef = useCallback(
    (node) => {
      if (courseLoading || getMoreCoursesStatus === 'request') return;
      if (courseObserver.current) courseObserver.current.disconnect();
      const extraQueries = {
        course_type: router?.query?.course_type || '',
        name: router?.query?.name || '',
        test: router?.query?.test || '',
        sort: router?.query?.sort || '',
      };
      courseObserver.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreCourse &&
          courseMeta?.last_pages >= courseQuery?.page
        ) {
          dispatch(
            setCourseQueryData({
              name: 'courses',
              value: {
                ...courseQuery,
                page: courseMeta?.current_page + 1,
                ...extraQueries,
              },
            })
          );
          dispatch(
            getCoursesByPage({
              ...courseQuery,
              page: courseMeta.current_page + 1,
              ...extraQueries,
            })
          );
        }
      });
      if (node) courseObserver.current.observe(node);
    },
    [getMoreCoursesStatus === 'request', courseLoading, hasMoreCourse, courseMeta]
  );

  const onHeartClick = (slug: string): void => {
    dispatch(addCourseToWishList(slug));
  };

  const ModalLoginOpen = (): void => {
    dispatch(
      setData({
        name: 'helperObj',
        key: 'loginModal',
        value: !loginModal,
      })
    );
  };

  return (
    <>
      {/* Login Modal If User is Not Logged In */}
      <Modal
        className="doubt-modalbox"
        style={{ zIndex: '100 !important' }}
        visible={loginModal}
        onCancel={() => ModalLoginOpen()}
        footer={[<></>]}
      >
        <div className="banner-form pl-4 pr-4">
          <Onboarding is_instructor={true} isLoginPage={false} />
        </div>
      </Modal>
      {loading || (total !== 0 && coursesData?.length === 0) ? (
        [...Array(1, 2, 3, 4, 5, 6)]?.map((i: number) => (
          <Col lg="4" md="6" key={i} className="mb-4">
            <div
              className="course-item blog"
              style={{
                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px',
                borderRadius: '16px',
              }}
            >
              <div
                className="course-image w-100"
                style={{
                  height: '200px',
                  background: 'unset',
                  borderTopLeftRadius: '16px',
                  borderTopRightRadius: '16px',
                }}
              >
                <Skeleton.Image className="w-100 h-100" />
              </div>
              <div className="course-content px-3 py-2">
                <div className="content-detail">
                  <Skeleton active />
                </div>
              </div>
            </div>
          </Col>
        ))
      ) : coursesData?.length === 0 && total === 0 ? (
        <div className="not-found">
          <Empty />
        </div>
      ) : (
        coursesData?.map((course, i) => (
          <Col lg="4" md="6" key={i} className="mb-4 p-0">
            <div ref={coursesData?.length === i + 1 ? lastCourseElementRef : null}>
              <CustomToolTip placement="right" title={!isMobile && !isTablet && ''}>
                <a
                  title={capitalizeFirstLetter(course?.name)}
                  href={`/course/${course.slug}`}
                >
                  <Card
                    className={`course-item-card border p-0`}
                    hoverable
                    style={{
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px',
                      borderRadius: '16px',
                    }}
                    cover={
                      <img
                        alt={course?.name}
                        style={{
                          height: '135px',
                          borderTopLeftRadius: '16px',
                          borderTopRightRadius: '16px',
                        }}
                        src={course?.image ? course?.image : '/images/course.png'}
                      />
                    }
                  >
                    <span className="ellipsis-clamp-2 course-single-name font-weight-500">
                      {capitalizeFirstLetter(course?.name)}
                    </span>
                    <div className="mt-2 mb-2">
                      <div className="position-relative d-flex align-items-center justify-content-between ">
                        <span className="course-instructor d-block">
                          {course?.test?.name ? (
                            <span
                              className="text-muted"
                              style={{ lineHeight: '0.5' }}
                            >
                              {capitalizeFirstLetter(course?.test?.name)}
                            </span>
                          ) : null}
                        </span>
                        <div
                          className="position-absolute float-right"
                          style={{
                            right: '1%',
                          }}
                        >
                          {course?.wishlist ? (
                            <HeartFilled
                              rev=""
                              className="text-danger cursor-pointer mt-1 heart-fill"
                              style={{ fontSize: '24px', zIndex: '1001' }}
                              title="Remove from wishlist"
                              onClick={(e) => {
                                e.preventDefault();
                                !isLogin()
                                  ? ModalLoginOpen()
                                  : onHeartClick(course?.slug);
                              }}
                            />
                          ) : (
                            <HeartOutlined
                              rev=""
                              className={
                                'text-dark mt-1 cursor-pointer heart-outline'
                              }
                              style={{ fontSize: '24px', zIndex: '1001' }}
                              title="Add to wishlist"
                              onClick={(e) => {
                                e.preventDefault();
                                !isLogin()
                                  ? ModalLoginOpen()
                                  : onHeartClick(course?.slug);
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="d-flex justify-content-between">
                        {course?.coming_soon ? (
                          <>
                            <Badge className="p-1 h-25 mt-2 bg-danger shadow-lg rounded">
                              <span
                                className="text-white"
                                style={{ fontSize: '13px', fontWeight: '500' }}
                              >
                                Coming Soon
                              </span>
                            </Badge>
                          </>
                        ) : (
                          <span className="course-rating mt-1">
                            {course?.total_feedback != 0 ? (
                              <>
                                <strong
                                  className="mr-1 mt-1"
                                  style={{
                                    fontSize: '12px',
                                    color: '#fbbc05',
                                  }}
                                >
                                  {course?.average_rating?.toFixed(1)}
                                </strong>
                                <Rate
                                  disabled
                                  allowHalf
                                  defaultValue={course?.average_rating}
                                  style={{
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: '#fbbc05',
                                  }}
                                />
                                <small
                                  className="ml-1 mt-1 text-muted font-weight-bold reviewtip"
                                  style={{
                                    fontSize: '12px',
                                    lineHeight: '1.8',
                                    fontFamily: 'Poppins sans-serif',
                                  }}
                                >
                                  ({course?.total_feedback})
                                  <span className="tooltiptext">No. of Review</span>
                                </small>
                              </>
                            ) : null}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 mb-3 border-bottom-dotted" />
                    <div className="position-relative d-flex justify-content-between">
                      <div className="d-flex flex-wrap align-items-center mt-1">
                        {fetchCourse?.loading ? (
                          <>
                            <Spinner animation="border" size="sm" />
                          </>
                        ) : (
                          <div className="price-text">
                            {findCourseObject(course?.id)?.is_free ? (
                              <span className=" font-weight-normal text-uppercase">
                                Free
                              </span>
                            ) : (
                              <>
                                <span className=" font-weight-normal text-dark text-uppercase">
                                  {findCourseObject(course?.id)?.currency === 'NPR'
                                    ? 'Rs.'
                                    : '$'}{' '}
                                  {(
                                    findCourseObject(course?.id)?.price -
                                    (findCourseObject(course?.id)
                                      ?.discount_percentage /
                                      100) *
                                      findCourseObject(course?.id)?.price
                                  ).toFixed(2)}
                                  /-{' '}
                                </span>
                                {findCourseObject(course?.id)
                                  ?.discount_percentage !== null &&
                                  parseInt(
                                    findCourseObject(course?.id)?.discount_percentage
                                  ) !== 0 && (
                                    <del className="ml-2 text-muted">
                                      {' '}
                                      {findCourseObject(course?.id)?.currency ===
                                      'NPR'
                                        ? 'Rs.'
                                        : '$'}
                                      {findCourseObject(course?.id)?.price}
                                    </del>
                                  )}
                              </>
                            )}
                            {/* Discount */}
                            {!course?.is_free &&
                              parseInt(course?.discount_percentage) !== 0 && (
                                <div className="discount-badge">
                                  <Badge.Ribbon
                                    className="shadow text-center"
                                    style={{
                                      fontSize: '12px',
                                      background: '#ffffff',
                                    }}
                                    text={`-${
                                      findCourseObject(course?.id)
                                        ?.discount_percentage
                                    }%`}
                                  />
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                      <div
                        className="go-to-course float-right position-absolute"
                        style={{
                          right: '1%',
                        }}
                      >
                        <ArrowRightOutlined
                          rev=""
                          style={{ fontSize: '20px', color: '#283360' }}
                        />
                      </div>
                    </div>
                  </Card>
                </a>
              </CustomToolTip>
            </div>
          </Col>
        ))
      )}
      {getMoreCoursesStatus === 'request' && (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" className="mt-3" />
        </div>
      )}
    </>
  );
};

export default memo(CourseItemGrid);
